/**
 * MeditationWallPage.jsx
 * 
 * Unity Meditation Wall (V2.0 - Hybrid Meditation Wall)
 * 
 * A room, not content. If it ever feels like a "tool," roll back.
 * 
 * Design Principles:
 * - No therapy language
 * - No spiritual instruction
 * - No explanation of the system
 * - Feature must feel optional, not corrective
 * - Entry transition must slow the app (ease animation)
 * - Retention via relief and return, not habit pressure
 * 
 * v2.0 Updates:
 * - Daily Anchor entry
 * - Guide Mode (retention by invitation)
 * - Quick Resets
 * - Progressive reveal sections
 * - Soft retention metrics ("Your Rhythm")
 * - Post-session check-in
 * - Renamed tile labels (display only)
 */

import React, { useState, useEffect, useRef, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Volume2, VolumeX, ChevronDown, ChevronUp, X } from 'lucide-react';
import { AuthContext } from '../App';
import apiClient from "../api/client";

const API = window.location.origin;

// Tile configuration - IDs unchanged, display labels updated
const TILES = [
  { id: 'now', label: 'PRESENT', color: 'from-slate-800/90 to-slate-900/90', accent: 'border-slate-600/40' },
  { id: 'move', label: 'BREATH', color: 'from-slate-800/90 to-zinc-900/90', accent: 'border-zinc-600/40' },
  { id: 'pace', label: 'CALM', color: 'from-zinc-800/90 to-slate-900/90', accent: 'border-slate-500/40' },
  { id: 'available', label: 'OPENNESS', color: 'from-slate-900/90 to-neutral-900/90', accent: 'border-neutral-600/40' },
  { id: 'true', label: 'AWARENESS', color: 'from-neutral-800/90 to-slate-900/90', accent: 'border-slate-600/40' },
  { id: 'release', label: 'RELEASE', color: 'from-slate-800/90 to-stone-900/90', accent: 'border-stone-600/40' },
  { id: 'clear', label: 'CLARITY', color: 'from-stone-800/90 to-slate-900/90', accent: 'border-slate-500/40' },
];

// Helper to get tile by ID
const getTileById = (id) => TILES.find(t => t.id === id);

// Time options in minutes
const TIME_OPTIONS = [2, 5, 10, 15];

// Clamp duration to valid time options
const clampDuration = (minutes) => {
  const sorted = [...TIME_OPTIONS].sort((a, b) => a - b);
  for (const opt of sorted) {
    if (minutes <= opt) return opt;
  }
  return sorted[sorted.length - 1];
};

// Opening lines (single sentence max, neutral, optional)
const OPENING_LINES = [
  'Begin when ready.',
  'This moment is yours.',
  'Breathe.',
  'Here.',
  'Now.',
  '',
  '',
  '', // Weighted toward silence
];

// Select opening line once per page load (outside component)
const getInitialOpeningLine = () => OPENING_LINES[Math.floor(Math.random() * OPENING_LINES.length)];

// LocalStorage keys
const LS_COMPLETIONS = 'meditation_wall_completions';
const LS_LAST_MOOD = 'meditation_wall_last_mood';
const LS_LIGHT_GUIDANCE = 'meditation_wall_light_guidance';
const LS_AMBIENT_SOUND = 'meditation_wall_ambient_sound'; // Stores { selection: string|null, enabled: boolean }

// Ambient sound options (one at a time, default off)
const AMBIENT_SOUNDS = [
  { id: 'rain', label: 'Rain', emoji: '🌧️' },
  { id: 'nature', label: 'Nature', emoji: '🌿' },
  { id: 'whitenoise', label: 'White Noise', emoji: '📻' },
];

// Guide mode options with clamped durations
const GUIDE_OPTIONS = [
  { id: 'overwhelmed', label: 'Overwhelmed', tileId: 'pace', duration: 2 },
  { id: 'restless', label: 'Restless', tileId: 'move', duration: 2 }, // clamped from 1
  { id: 'heavy', label: 'Emotionally heavy', tileId: 'release', duration: 5 },
  { id: 'disconnected', label: 'Disconnected', tileId: 'available', duration: 5 }, // clamped from 3
  { id: 'quiet', label: 'Just need quiet', tileId: 'clear', duration: 2 },
];

// Quick reset options
const QUICK_RESETS = [
  { id: 'breath', label: 'Breath Reset', tileId: 'move', duration: 2 },
  { id: 'calm', label: 'Quick Calm', tileId: 'pace', duration: 2 },
  { id: 'focus', label: 'Focus', tileId: 'clear', duration: 2 },
  { id: 'sleep', label: 'Sleep', tileId: 'release', duration: 5 },
];

// Meditations for real moments
const REAL_MOMENTS = [
  { label: 'Before responding', tileId: 'move', duration: 2 },
  { label: 'After being left on read', tileId: 'pace', duration: 2 },
  { label: 'Jealousy cooldown', tileId: 'true', duration: 5 },
  { label: 'After conflict', tileId: 'release', duration: 5 },
  { label: 'Opening to intimacy', tileId: 'available', duration: 5 },
  { label: 'Letting go', tileId: 'release', duration: 5 },
];

// Micro meditations
const MICRO_MEDITATIONS = [
  { label: 'One Breath', tileId: 'move', duration: 2 },
  { label: 'Body Scan', tileId: 'true', duration: 2 },
  { label: 'Grounding', tileId: 'now', duration: 2 },
  { label: 'Release Tension', tileId: 'release', duration: 2 },
];

export default function MeditationWallPage() {
  const navigate = useNavigate();
  const { token } = useContext(AuthContext);
  
  // States
  const [selectedTile, setSelectedTile] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [lightGuidance, setLightGuidance] = useState(() => {
    try {
      return localStorage.getItem(LS_LIGHT_GUIDANCE) === 'true';
    } catch {
      return false;
    }
  });
  const [inSession, setInSession] = useState(false);
  const [sessionPhase, setSessionPhase] = useState('idle'); // idle, opening, active, closing, complete
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [fadeIn, setFadeIn] = useState(false);
  const [sessionOpeningLine, setSessionOpeningLine] = useState('');
  
  // New states for v2.0
  const [showGuideModal, setShowGuideModal] = useState(false);
  const [showMoreSections, setShowMoreSections] = useState(false);
  const [skipConfirmation, setSkipConfirmation] = useState(false);
  const [checkinMood, setCheckinMood] = useState(null);
  const [rhythmStats, setRhythmStats] = useState({ today: 0, week: 0 });
  
  // Ambient sound state: { selection: string|null, enabled: boolean }
  // - selection: which sound is selected (persisted to localStorage)
  // - enabled: whether sound is currently playing (NOT persisted - always false on load)
  const [ambientSound, setAmbientSound] = useState(() => {
    try {
      const stored = localStorage.getItem(LS_AMBIENT_SOUND);
      if (stored) {
        const parsed = JSON.parse(stored);
        // Restore selection, but always start with enabled=false (rule 7: no autoplay on load)
        return { selection: parsed.selection || null, enabled: false };
      }
    } catch { /* ignore */ }
    return { selection: null, enabled: false };
  });
  const [ambientPlaying, setAmbientPlaying] = useState(false); // Actual playback state
  
  // Refs
  const timerRef = useRef(null);
  const ambientAudioRef = useRef(null);
  const ambientAudioCtxRef = useRef(null);
  const ambientNodesRef = useRef(null);

  // Utility: create noise buffers with "color" (white/pink/brown)
  // Pink noise is typically more calming than white noise (less harsh high-frequency energy).
  // Brown noise is deeper/warmer and works well for wind beds.
  const createNoiseBuffer = (audioCtx, color = "white") => {
    const bufferSize = 2 * audioCtx.sampleRate;
    const noiseBuffer = audioCtx.createBuffer(1, bufferSize, audioCtx.sampleRate);
    const output = noiseBuffer.getChannelData(0);

    if (color === "white") {
      for (let i = 0; i < bufferSize; i++) output[i] = Math.random() * 2 - 1;
      return noiseBuffer;
    }

    if (color === "brown") {
      // Brownian noise: integrate white noise (with damping)
      let lastOut = 0;
      for (let i = 0; i < bufferSize; i++) {
        const white = Math.random() * 2 - 1;
        lastOut = (lastOut + 0.02 * white) / 1.02;
        output[i] = lastOut * 3.5; // normalize-ish
      }
      return noiseBuffer;
    }

    // Pink noise (Paul Kellet method)
    let b0 = 0, b1 = 0, b2 = 0, b3 = 0, b4 = 0, b5 = 0, b6 = 0;
    for (let i = 0; i < bufferSize; i++) {
      const white = Math.random() * 2 - 1;
      b0 = 0.99886 * b0 + white * 0.0555179;
      b1 = 0.99332 * b1 + white * 0.0750759;
      b2 = 0.96900 * b2 + white * 0.1538520;
      b3 = 0.86650 * b3 + white * 0.3104856;
      b4 = 0.55000 * b4 + white * 0.5329522;
      b5 = -0.7616 * b5 - white * 0.0168980;
      const pink = b0 + b1 + b2 + b3 + b4 + b5 + b6 + white * 0.5362;
      b6 = white * 0.115926;
      output[i] = pink * 0.11; // scale down to avoid clipping
    }
    return noiseBuffer;
  };
  
  // Load rhythm stats from localStorage
  useEffect(() => {
    try {
      const stored = localStorage.getItem(LS_COMPLETIONS);
      if (stored) {
        const data = JSON.parse(stored);
        const today = new Date().toISOString().split('T')[0];
        const todayCount = data[today] || 0;
        
        // Calculate week count (today + previous 6 days)
        let weekCount = 0;
        for (let i = 0; i < 7; i++) {
          const d = new Date();
          d.setDate(d.getDate() - i);
          const key = d.toISOString().split('T')[0];
          weekCount += data[key] || 0;
        }
        
        setRhythmStats({ today: todayCount, week: weekCount });
      }
    } catch {
      // Silent fail
    }
  }, [inSession]); // Refresh after session ends
  
  // Persist light guidance preference
  useEffect(() => {
    try {
      localStorage.setItem(LS_LIGHT_GUIDANCE, lightGuidance.toString());
    } catch {
      // Silent fail
    }
  }, [lightGuidance]);
  
  // Persist ambient sound SELECTION only (not enabled state) - rule 7
  useEffect(() => {
    try {
      if (ambientSound.selection) {
        localStorage.setItem(LS_AMBIENT_SOUND, JSON.stringify({ selection: ambientSound.selection }));
      } else {
        localStorage.removeItem(LS_AMBIENT_SOUND);
      }
    } catch {
      // Silent fail
    }
  }, [ambientSound.selection]);
  
  // Create ambient sound using Web Audio API (only called on explicit user action)
  const createAmbientSound = (type) => {
    // Rule 6: Don't create anything if type is null/undefined
    if (!type) return;
    
    try {
      // Stop any existing ambient sound first
      stopAmbientSound();
      
      // Rule 5: Mobile-safe - AudioContext requires user gesture on iOS/Android
      // This function is only called from user tap handlers, so we're safe
      const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
      
      // iOS Safari may start in 'suspended' state - resume it
      if (audioCtx.state === 'suspended') {
        audioCtx.resume();
      }
      
      ambientAudioCtxRef.current = audioCtx;
      
      const gainNode = audioCtx.createGain();
      gainNode.gain.setValueAtTime(0, audioCtx.currentTime);
      // Overall volume was too loud/harsh on mobile. Lower master level.
      gainNode.gain.linearRampToValueAtTime(0.085, audioCtx.currentTime + 2); // Fade in
      gainNode.connect(audioCtx.destination);

      // Create buffers (colored) up front
      const pinkNoiseBuffer = createNoiseBuffer(audioCtx, "pink");
      const brownNoiseBuffer = createNoiseBuffer(audioCtx, "brown");
      
      if (type === 'whitenoise') {
        // Calming "white noise" for users = pink noise (less hissy).
        const whiteNoise = audioCtx.createBufferSource();
        whiteNoise.buffer = pinkNoiseBuffer;
        whiteNoise.loop = true;
        // Gentle tilt: reduce extreme highs a bit
        const lowpass = audioCtx.createBiquadFilter();
        lowpass.type = "lowpass";
        lowpass.frequency.value = 9000;
        lowpass.Q.value = 0.2;
        whiteNoise.connect(lowpass);
        lowpass.connect(gainNode);
        whiteNoise.start();
        ambientNodesRef.current = { source: whiteNoise, filter: lowpass, gain: gainNode };
        
      } else if (type === 'rain') {
        // Rain: smooth pink-noise bed + soft band-limited droplets + gentle stereo motion.
        const bed = audioCtx.createBufferSource();
        bed.buffer = pinkNoiseBuffer;
        bed.loop = true;

        const bedLowpass = audioCtx.createBiquadFilter();
        bedLowpass.type = "lowpass";
        bedLowpass.frequency.value = 1800;
        bedLowpass.Q.value = 0.25;

        const bedHighpass = audioCtx.createBiquadFilter();
        bedHighpass.type = "highpass";
        bedHighpass.frequency.value = 120;
        bedHighpass.Q.value = 0.2;

        const bedPanner = audioCtx.createStereoPanner();
        bedPanner.pan.value = 0;

        // slow pan for realism (very subtle)
        const panLfo = audioCtx.createOscillator();
        const panLfoGain = audioCtx.createGain();
        panLfo.frequency.value = 0.03;
        panLfoGain.gain.value = 0.18;
        panLfo.connect(panLfoGain);
        panLfoGain.connect(bedPanner.pan);

        bed.connect(bedHighpass);
        bedHighpass.connect(bedLowpass);
        bedLowpass.connect(bedPanner);
        bedPanner.connect(gainNode);

        // Droplets: short bandpassed noise bursts, panned randomly (NOT hiss spikes)
        const dropBand = audioCtx.createBiquadFilter();
        dropBand.type = "bandpass";
        dropBand.frequency.value = 2600;
        dropBand.Q.value = 6.0;

        const dropPanner = audioCtx.createStereoPanner();
        dropPanner.pan.value = 0;

        const dropGain = audioCtx.createGain();
        dropGain.gain.value = 0.0;
        dropBand.connect(dropPanner);
        dropPanner.connect(dropGain);
        dropGain.connect(gainNode);

        const makeDroplet = () => {
          const src = audioCtx.createBufferSource();
          src.buffer = pinkNoiseBuffer;
          const t = audioCtx.currentTime;
          const amp = 0.010 + Math.random() * 0.016; // much softer than before
          const pan = (Math.random() * 2 - 1) * 0.65;

          dropPanner.pan.setValueAtTime(pan, t);
          // slightly vary droplet "timbre"
          dropBand.frequency.setValueAtTime(2000 + Math.random() * 1800, t);
          dropBand.Q.setValueAtTime(5.0 + Math.random() * 4.0, t);

          try {
            dropGain.gain.cancelScheduledValues(t);
            dropGain.gain.setValueAtTime(0.0001, t);
            dropGain.gain.linearRampToValueAtTime(amp, t + 0.006);
            dropGain.gain.exponentialRampToValueAtTime(0.0001, t + 0.09);
          } catch { /* ignore */ }

          src.connect(dropBand);
          try { src.start(t); } catch { /* ignore */ }
          try { src.stop(t + 0.11); } catch { /* ignore */ }
        };

        const dropletTimer = setInterval(() => {
          // 0–2 droplets per second; more sparse, more realistic
          const count = Math.random() < 0.4 ? 0 : (Math.random() < 0.7 ? 1 : 2);
          for (let i = 0; i < count; i++) setTimeout(makeDroplet, Math.random() * 550);
        }, 1000);

        panLfo.start();
        bed.start();

        ambientNodesRef.current = {
          source: bed,
          gain: gainNode,
          dropletTimer,
          panLfo,
          bedLowpass,
          bedHighpass,
          bedPanner
        };
        
      } else if (type === 'nature') {
        // Nature: warm brown-noise wind bed + occasional soft bird chirps (clearly distinct from rain).
        const wind = audioCtx.createBufferSource();
        wind.buffer = brownNoiseBuffer;
        wind.loop = true;

        const windHighpass = audioCtx.createBiquadFilter();
        windHighpass.type = "highpass";
        windHighpass.frequency.value = 60;
        windHighpass.Q.value = 0.25;

        const windLowpass = audioCtx.createBiquadFilter();
        windLowpass.type = "lowpass";
        windLowpass.frequency.value = 650;
        windLowpass.Q.value = 0.25;

        const windGain = audioCtx.createGain();
        windGain.gain.value = 0.55; // nature bed slightly lower than master

        const windPanner = audioCtx.createStereoPanner();
        windPanner.pan.value = 0;

        // gust modulation on wind gain (subtle breathing)
        const gustLfo = audioCtx.createOscillator();
        const gustDepth = audioCtx.createGain();
        gustLfo.frequency.value = 0.05;
        gustDepth.gain.value = 0.18;
        gustLfo.connect(gustDepth);
        gustDepth.connect(windGain.gain);

        wind.connect(windHighpass);
        windHighpass.connect(windLowpass);
        windLowpass.connect(windGain);
        windGain.connect(windPanner);
        windPanner.connect(gainNode);

        // Bird chirps: triangle/sine-like, panned, very soft (not synthy beeps)
        const chirpBus = audioCtx.createGain();
        chirpBus.gain.value = 0.10;
        chirpBus.connect(gainNode);

        const playChirp = () => {
          const t = audioCtx.currentTime;
          const osc = audioCtx.createOscillator();
          const env = audioCtx.createGain();
          const pan = audioCtx.createStereoPanner();

          osc.type = "triangle";
          const startF = 900 + Math.random() * 500;
          const endF = 1700 + Math.random() * 900;
          osc.frequency.setValueAtTime(startF, t);
          osc.frequency.exponentialRampToValueAtTime(endF, t + 0.10);
          osc.frequency.exponentialRampToValueAtTime(startF * 0.95, t + 0.20);

          pan.pan.setValueAtTime((Math.random() * 2 - 1) * 0.7, t);

          env.gain.setValueAtTime(0.0001, t);
          env.gain.linearRampToValueAtTime(0.06 + Math.random() * 0.04, t + 0.015);
          env.gain.exponentialRampToValueAtTime(0.0001, t + 0.30);

          osc.connect(env);
          env.connect(pan);
          pan.connect(chirpBus);
          try { osc.start(t); } catch { /* ignore */ }
          try { osc.stop(t + 0.32); } catch { /* ignore */ }
        };

        const chirpTimer = setInterval(() => {
          // More natural cadence: sometimes quiet for a while
          if (Math.random() < 0.35) playChirp();
        }, 7000);

        gustLfo.start();
        wind.start();

        ambientNodesRef.current = {
          source: wind,
          gain: gainNode,
          chirpTimer,
          gustLfo,
          windHighpass,
          windLowpass,
          windGain,
          windPanner
        };
      }
      
      setAmbientPlaying(true);
    } catch {
      // Silent fail - audio not critical
      setAmbientPlaying(false);
    }
  };
  
  // Stop ambient sound - rule 4: stop on session end and navigation/unmount
  const stopAmbientSound = () => {
    setAmbientPlaying(false);
    
    try {
      if (ambientNodesRef.current) {
        const { source, source2, lfo, gain, dropletTimer, chirpTimer, panLfo, gustLfo } = ambientNodesRef.current;
        // Clear any scheduled timers driving transients/chirps
        try { if (dropletTimer) clearInterval(dropletTimer); } catch { /* ignore */ }
        try { if (chirpTimer) clearInterval(chirpTimer); } catch { /* ignore */ }
        try { if (panLfo) panLfo.stop(); } catch { /* ignore */ }
        try { if (gustLfo) gustLfo.stop(); } catch { /* ignore */ }
        // Fade out quickly
        if (gain && ambientAudioCtxRef.current && ambientAudioCtxRef.current.state !== 'closed') {
          try {
            gain.gain.linearRampToValueAtTime(0, ambientAudioCtxRef.current.currentTime + 0.3);
          } catch { /* ignore */ }
        }
        // Stop sources after fade
        setTimeout(() => {
          try { if (source) source.stop(); } catch { /* Already stopped */ }
          try { if (source2) source2.stop(); } catch { /* Already stopped */ }
          try { if (lfo) lfo.stop(); } catch { /* Already stopped */ }
        }, 400);
        ambientNodesRef.current = null;
      }
      
      // Close AudioContext to free resources - rule 6: no CPU burn when off
      if (ambientAudioCtxRef.current) {
        const ctx = ambientAudioCtxRef.current;
        ambientAudioCtxRef.current = null;
        setTimeout(() => {
          try {
            if (ctx.state !== 'closed') ctx.close();
          } catch { /* Already closed */ }
        }, 500);
      }
    } catch {
      // Silent fail
    }
  };
  
  // Toggle ambient sound selection (rule 2: mutually exclusive)
  const toggleAmbientSound = (soundId) => {
    if (ambientSound.selection === soundId && ambientSound.enabled) {
      // Same sound, currently enabled -> turn off
      setAmbientSound({ selection: soundId, enabled: false });
      stopAmbientSound();
    } else if (ambientSound.selection === soundId && !ambientSound.enabled) {
      // Same sound, currently disabled -> enable it
      setAmbientSound({ selection: soundId, enabled: true });
      // Rule 3: Only start if in active session (explicit user tap starts it)
      if (inSession && sessionPhase === 'active') {
        createAmbientSound(soundId);
      }
    } else {
      // Different sound -> switch to it and enable
      // First stop any playing sound
      stopAmbientSound();
      setAmbientSound({ selection: soundId, enabled: true });
      // Rule 3: Only start if in active session
      if (inSession && sessionPhase === 'active') {
        createAmbientSound(soundId);
      }
    }
  };
  
  // Entry animation - slow the app
  useEffect(() => {
    const timer = setTimeout(() => setFadeIn(true), 100);
    return () => clearTimeout(timer);
  }, []);
  
  // Handle tile tap -> show time selection with haptic
  const handleTileTap = (tile) => {
    if (navigator.vibrate) {
      navigator.vibrate(10);
    }
    setSelectedTile(tile);
    setSelectedTime(null);
    setSkipConfirmation(false);
  };
  
  // Handle time selection -> start session
  const handleTimeSelect = (minutes) => {
    setSelectedTime(minutes);
  };
  
  // Quick start - skip confirmation overlays
  const quickStart = (tileId, duration) => {
    const tile = getTileById(tileId);
    if (!tile) return;
    
    if (navigator.vibrate) navigator.vibrate(10);
    
    setSelectedTile(tile);
    setSelectedTime(duration);
    setSkipConfirmation(true);
    
    // Start immediately
    setTimeout(() => {
      startSessionDirect(tile, duration);
    }, 50);
  };
  
  // Start session directly (used by quickStart)
  const startSessionDirect = (tile, time) => {
    setSessionOpeningLine(getInitialOpeningLine());
    setInSession(true);
    setSessionPhase('opening');
    setTimeRemaining(time * 60);
    setCheckinMood(null);
    
    playBell();
    
    setTimeout(() => {
      setSessionPhase('active');
      startTimer(time);
      
      // Rule 3: Only start ambient sound if user has explicitly enabled it
      if (ambientSound.selection && ambientSound.enabled) {
        createAmbientSound(ambientSound.selection);
      }
      
      if (lightGuidance && ambientAudioRef.current) {
        ambientAudioRef.current.play().catch(() => {});
      }
    }, 3000);
  };
  
  // Start session (from confirmation overlay)
  const startSession = () => {
    if (!selectedTile || !selectedTime) return;
    startSessionDirect(selectedTile, selectedTime);
  };
  
  // Timer logic
  const startTimer = (time) => {
    timerRef.current = setInterval(() => {
      setTimeRemaining((prev) => {
        if (prev <= 1) {
          clearInterval(timerRef.current);
          endSession();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };
  
  // End session
  const endSession = () => {
    setSessionPhase('closing');
    
    // Stop ambient sounds
    stopAmbientSound();
    
    if (ambientAudioRef.current) {
      ambientAudioRef.current.pause();
      ambientAudioRef.current.currentTime = 0;
    }
    
    playBell();
    logMeditationComplete();
    incrementCompletionCount();
    
    setTimeout(() => {
      setSessionPhase('complete');
    }, 2000);
  };
  
  // Increment completion count in localStorage
  const incrementCompletionCount = () => {
    try {
      const stored = localStorage.getItem(LS_COMPLETIONS);
      const data = stored ? JSON.parse(stored) : {};
      const today = new Date().toISOString().split('T')[0];
      data[today] = (data[today] || 0) + 1;
      localStorage.setItem(LS_COMPLETIONS, JSON.stringify(data));
    } catch {
      // Silent fail
    }
  };
  
  // Play bell sound (soft, subtle)
  const playBell = () => {
    try {
      const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
      const oscillator = audioCtx.createOscillator();
      const gainNode = audioCtx.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioCtx.destination);
      
      oscillator.frequency.value = 528;
      oscillator.type = 'sine';
      
      gainNode.gain.setValueAtTime(0.3, audioCtx.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioCtx.currentTime + 2);
      
      oscillator.start(audioCtx.currentTime);
      oscillator.stop(audioCtx.currentTime + 2);
    } catch {
      // Silent fail
    }
  };
  
  // Log meditation completion to backend
  const logMeditationComplete = async () => {
    try {
      await apiClient.post(
        `/meditation/complete`,
        {
          tile_id: selectedTile?.id,
          duration_minutes: selectedTime,
          light_guidance: lightGuidance,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } catch {
      // Silent fail
    }
  };
  
  // Handle mood selection
  const handleMoodSelect = (mood) => {
    setCheckinMood(mood);
    try {
      localStorage.setItem(LS_LAST_MOOD, JSON.stringify({
        mood,
        timestamp: new Date().toISOString(),
      }));
    } catch {
      // Silent fail
    }
  };
  
  // Return to wall
  const returnToWall = () => {
    setInSession(false);
    setSessionPhase('idle');
    setSelectedTile(null);
    setSelectedTime(null);
    setTimeRemaining(0);
    setSkipConfirmation(false);
    setCheckinMood(null);
    clearInterval(timerRef.current);
    stopAmbientSound();
  };
  
  // Format time remaining
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };
  
  // Cleanup on unmount - rule 4: stop audio on navigation/unmount
  useEffect(() => {
    const ambientAudio = ambientAudioRef.current;
    return () => {
      // Clear timer
      clearInterval(timerRef.current);
      
      // Stop any ambient sound and close AudioContext
      // (inline to avoid stale closure issues)
      try {
        if (ambientNodesRef.current) {
          const { source, source2, lfo } = ambientNodesRef.current;
          try { if (source) source.stop(); } catch { /* ignore */ }
          try { if (source2) source2.stop(); } catch { /* ignore */ }
          try { if (lfo) lfo.stop(); } catch { /* ignore */ }
          ambientNodesRef.current = null;
        }
        if (ambientAudioCtxRef.current) {
          try { ambientAudioCtxRef.current.close(); } catch { /* ignore */ }
          ambientAudioCtxRef.current = null;
        }
      } catch { /* ignore */ }
      
      if (ambientAudio) {
        ambientAudio.pause();
      }
    };
  }, []);
  
  // ============================================================================
  // SESSION VIEW
  // ============================================================================
  if (inSession) {
    return (
      <div 
        className="fixed inset-0 bg-black flex items-center justify-center z-50 transition-opacity duration-1000"
        style={{ opacity: sessionPhase === 'idle' ? 0 : 1 }}
      >
        {/* Early Exit (return to wall, not dashboard) */}
        <button
          onClick={() => {
            // End the session early and return to the wall view.
            // This prevents "back" from dumping users back to Dashboard.
            returnToWall();
          }}
          className="absolute top-6 right-6 text-white/30 hover:text-white/60 transition-colors"
          aria-label="Exit session"
          title="Exit session"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Opening Phase */}
        {sessionPhase === 'opening' && (
          <div className="text-center animate-fade-in">
            <p className="text-white/60 text-lg font-light tracking-wide">
              {sessionOpeningLine}
            </p>
          </div>
        )}
        
        {/* Active Phase */}
        {sessionPhase === 'active' && (
          <div className="text-center">
            <div className="relative w-48 h-48 mx-auto mb-8">
              <svg className="w-full h-full transform -rotate-90">
                <circle
                  cx="96"
                  cy="96"
                  r="88"
                  stroke="rgba(255,255,255,0.1)"
                  strokeWidth="2"
                  fill="none"
                />
                <circle
                  cx="96"
                  cy="96"
                  r="88"
                  stroke="rgba(255,255,255,0.3)"
                  strokeWidth="2"
                  fill="none"
                  strokeDasharray={2 * Math.PI * 88}
                  strokeDashoffset={2 * Math.PI * 88 * (1 - timeRemaining / (selectedTime * 60))}
                  className="transition-all duration-1000 ease-linear"
                />
              </svg>
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="text-white/40 text-2xl font-light tabular-nums">
                  {formatTime(timeRemaining)}
                </span>
              </div>
            </div>
          </div>
        )}
        
        {/* Closing Phase */}
        {sessionPhase === 'closing' && (
          <div className="text-center animate-fade-in">
            <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-white/5 flex items-center justify-center">
              <div className="w-3 h-3 bg-white/30 rounded-full" />
            </div>
          </div>
        )}
        
        {/* Complete Phase */}
        {sessionPhase === 'complete' && (
          <div className="text-center animate-fade-in px-6" data-testid="meditation-checkin">
            <p className="text-white/70 text-lg mb-8 font-light">
              Session complete.
            </p>
            
            {/* Post-session check-in */}
            {!checkinMood && (
              <div className="mb-10">
                <p className="text-white/40 text-sm mb-4">How do you feel now?</p>
                <div className="flex justify-center gap-6">
                  <button
                    onClick={() => handleMoodSelect('happy')}
                    className="text-3xl hover:scale-110 transition-transform"
                    aria-label="Feeling good"
                  >
                    🙂
                  </button>
                  <button
                    onClick={() => handleMoodSelect('neutral')}
                    className="text-3xl hover:scale-110 transition-transform"
                    aria-label="Feeling neutral"
                  >
                    😐
                  </button>
                  <button
                    onClick={() => handleMoodSelect('rough')}
                    className="text-3xl hover:scale-110 transition-transform"
                    aria-label="Feeling rough"
                  >
                    😣
                  </button>
                </div>
              </div>
            )}
            
            {/* After mood selection */}
            {checkinMood && (
              <div className="mb-10 animate-fade-in">
                <p className="text-white/50 text-sm mb-3">Carry this with you.</p>
                <p className="text-white/30 text-xs">
                  Suggested returns: Before sleep · Before responding · Midday reset
                </p>
              </div>
            )}
            
            <div className="flex flex-col gap-4">
              <button
                onClick={returnToWall}
                className="px-8 py-3 rounded-full bg-white/10 text-white/70 hover:bg-white/15 transition-colors"
                data-testid="meditation-return-wall"
              >
                Return to Wall
              </button>
              <button
                onClick={() => navigate('/dashboard')}
                className="px-8 py-3 rounded-full text-white/40 hover:text-white/60 transition-colors"
                data-testid="meditation-exit"
              >
                Exit
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }
  
  // ============================================================================
  // WALL VIEW
  // ============================================================================
  return (
    <div 
      className={`min-h-screen bg-black transition-opacity duration-700 ease-out relative overflow-hidden ${fadeIn ? 'opacity-100' : 'opacity-0'}`}
    >
      {/* Background: Vignette + Grain for depth */}
      <div className="fixed inset-0 pointer-events-none">
        <div 
          className="absolute inset-0"
          style={{
            background: 'radial-gradient(ellipse at center, transparent 0%, transparent 50%, rgba(0,0,0,0.15) 100%)',
          }}
        />
        <div 
          className="absolute inset-0 opacity-[0.04]"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`,
          }}
        />
      </div>
      
      {/* Guide Modal */}
      {showGuideModal && (
        <div 
          className="fixed inset-0 bg-black/95 flex items-center justify-center z-50 animate-fade-in"
          data-testid="meditation-guide-modal"
        >
          <div className="text-center px-6 max-w-sm">
            <button
              onClick={() => setShowGuideModal(false)}
              className="absolute top-6 right-6 text-white/30 hover:text-white/50"
            >
              <X className="w-5 h-5" />
            </button>
            
            <p className="text-white/60 text-lg mb-8 font-light">
              What feels closest right now?
            </p>
            
            <div className="flex flex-col gap-3">
              {GUIDE_OPTIONS.map((opt) => (
                <button
                  key={opt.id}
                  onClick={() => {
                    setShowGuideModal(false);
                    quickStart(opt.tileId, opt.duration);
                  }}
                  className="py-4 px-6 rounded-xl bg-white/5 hover:bg-white/10 text-white/60 hover:text-white/80 transition-all text-left"
                  data-testid={`meditation-guide-choice-${opt.id}`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
      
      {/* Header - minimal */}
      <header className="relative z-10 px-4 py-6">
        <button
          onClick={() => navigate(-1)}
          className="text-white/30 hover:text-white/50 transition-colors"
          data-testid="meditation-back"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
      </header>
      
      {/* Time Selection Overlay */}
      {selectedTile && !selectedTime && !skipConfirmation && (
        <div className="fixed inset-0 bg-black/90 flex items-center justify-center z-40 animate-fade-in">
          <div className="text-center px-6">
            <p className="text-white/40 text-sm uppercase tracking-widest mb-8">
              {selectedTile.label}
            </p>
            
            <div className="grid grid-cols-2 gap-4 max-w-xs mx-auto mb-8">
              {TIME_OPTIONS.map((mins) => (
                <button
                  key={mins}
                  onClick={() => {
                    if (navigator.vibrate) navigator.vibrate(10);
                    handleTimeSelect(mins);
                  }}
                  className="py-4 px-6 rounded-xl bg-white/5 hover:bg-white/10 text-white/60 hover:text-white/80 transition-all active:scale-[0.98]"
                  data-testid={`meditation-time-${mins}`}
                >
                  {mins} min
                </button>
              ))}
            </div>
            
            {/* Light guidance toggle */}
            <button
              onClick={() => setLightGuidance(!lightGuidance)}
              className={`flex items-center gap-2 mx-auto px-4 py-2 rounded-full transition-colors ${
                lightGuidance ? 'text-white/60' : 'text-white/30'
              }`}
              data-testid="meditation-guidance-toggle"
            >
              {lightGuidance ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
              <span className="text-sm">Light guidance</span>
            </button>
            
            {/* Cancel */}
            <button
              onClick={() => setSelectedTile(null)}
              className="mt-8 text-white/20 hover:text-white/40 text-sm transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
      
      {/* Start Session Confirmation */}
      {selectedTile && selectedTime && !skipConfirmation && (
        <div className="fixed inset-0 bg-black/95 flex items-center justify-center z-40 animate-fade-in">
          <div className="text-center px-6">
            <p className="text-white/40 text-sm uppercase tracking-widest mb-2">
              {selectedTile.label}
            </p>
            <p className="text-white/60 text-2xl font-light mb-12">
              {selectedTime} minutes
            </p>
            
            <button
              onClick={() => {
                if (navigator.vibrate) navigator.vibrate(10);
                startSession();
              }}
              className="px-12 py-4 rounded-full bg-white/10 hover:bg-white/15 text-white/70 hover:text-white/90 transition-all active:scale-[0.98]"
              data-testid="meditation-start"
            >
              Begin
            </button>
            
            <button
              onClick={() => setSelectedTime(null)}
              className="block mt-6 mx-auto text-white/20 hover:text-white/40 text-sm transition-colors"
            >
              Back
            </button>
          </div>
        </div>
      )}
      
      {/* Main Content */}
      <main className="relative z-10 px-4 pb-8">
        <div className="max-w-md mx-auto">
          
          {/* Daily Anchor */}
          <div 
            className="mb-8 p-6 rounded-2xl bg-white/[0.03] border border-white/[0.06]"
            data-testid="meditation-daily-anchor"
          >
            <h2 className="text-white/70 text-lg font-light mb-1">Today's Meditation</h2>
            <p className="text-white/40 text-sm mb-4">A short practice to return to yourself</p>
            <button
              onClick={() => quickStart('clear', 2)}
              className="px-6 py-3 rounded-full bg-white/10 hover:bg-white/15 text-white/70 hover:text-white/90 transition-all text-sm"
              data-testid="meditation-daily-anchor-start"
            >
              Begin – 2 min
            </button>
          </div>
          
          {/* Grid Framing Copy */}
          <p 
            className="text-white/30 text-sm text-center mb-4"
            data-testid="meditation-grid-framing"
          >
            Choose what you need in this moment.
          </p>
          
          {/* Tile Grid - First 6 tiles in 2-column grid */}
          <div className="grid grid-cols-2 gap-3">
            {TILES.slice(0, 6).map((tile, index) => (
              <button
                key={tile.id}
                onClick={() => handleTileTap(tile)}
                className="meditation-tile group aspect-square rounded-2xl relative overflow-hidden transition-all duration-300 ease-out hover:scale-[1.02] active:scale-[0.98]"
                style={{
                  animationDelay: `${index * 80}ms`,
                }}
                data-testid={`meditation-tile-${tile.id}`}
              >
                {/* Tile glass treatment with depth */}
                <div className={`absolute inset-0 bg-gradient-to-br ${tile.color} border ${tile.accent} rounded-2xl`}>
                  <div className="absolute inset-0 rounded-2xl" style={{
                    boxShadow: 'inset 2px 2px 8px rgba(0,0,0,0.3), inset -1px -1px 4px rgba(255,255,255,0.02)',
                  }} />
                  <div className="absolute top-0 left-0 right-1/2 h-px bg-gradient-to-r from-white/10 to-transparent rounded-t-2xl" />
                  <div className="absolute top-0 left-0 bottom-1/2 w-px bg-gradient-to-b from-white/10 to-transparent rounded-l-2xl" />
                </div>
                
                {/* Breathing glow animation */}
                <div 
                  className="absolute inset-0 rounded-2xl opacity-0 meditation-breathe"
                  style={{
                    background: 'radial-gradient(ellipse at center, rgba(255,255,255,0.03) 0%, transparent 70%)',
                    animationDelay: `${index * 2.5}s`,
                  }}
                />
                
                {/* Specular sweep on press */}
                <div className="absolute inset-0 rounded-2xl opacity-0 group-active:opacity-100 transition-opacity duration-150 pointer-events-none"
                  style={{
                    background: 'linear-gradient(105deg, transparent 40%, rgba(255,255,255,0.05) 50%, transparent 60%)',
                  }}
                />
                
                {/* Label */}
                <span className="absolute inset-0 flex items-center justify-center text-white/55 text-sm font-light tracking-widest">
                  {tile.label}
                </span>
              </button>
            ))}
          </div>
          
          {/* 7th tile (CLARITY) - intentional anchor with halo */}
          <div className="flex justify-center mt-3">
            <button
              onClick={() => handleTileTap(TILES[6])}
              className="meditation-tile group relative overflow-hidden transition-all duration-300 ease-out hover:scale-[1.02] active:scale-[0.98]"
              style={{
                width: 'calc(50% + 8px)',
                aspectRatio: '1',
                animationDelay: '480ms',
              }}
              data-testid={`meditation-tile-${TILES[6].id}`}
            >
              {/* Soft halo glow behind CLARITY */}
              <div className="absolute -inset-4 rounded-3xl opacity-30"
                style={{
                  background: 'radial-gradient(ellipse at center, rgba(100,116,139,0.15) 0%, transparent 70%)',
                }}
              />
              
              {/* Tile glass treatment */}
              <div className={`absolute inset-0 bg-gradient-to-br ${TILES[6].color} border ${TILES[6].accent} rounded-2xl`}>
                <div className="absolute inset-0 rounded-2xl" style={{
                  boxShadow: 'inset 2px 2px 8px rgba(0,0,0,0.3), inset -1px -1px 4px rgba(255,255,255,0.02)',
                }} />
                <div className="absolute top-0 left-0 right-1/2 h-px bg-gradient-to-r from-white/10 to-transparent rounded-t-2xl" />
                <div className="absolute top-0 left-0 bottom-1/2 w-px bg-gradient-to-b from-white/10 to-transparent rounded-l-2xl" />
              </div>
              
              {/* Breathing glow */}
              <div 
                className="absolute inset-0 rounded-2xl opacity-0 meditation-breathe"
                style={{
                  background: 'radial-gradient(ellipse at center, rgba(255,255,255,0.03) 0%, transparent 70%)',
                  animationDelay: '15s',
                }}
              />
              
              {/* Specular sweep */}
              <div className="absolute inset-0 rounded-2xl opacity-0 group-active:opacity-100 transition-opacity duration-150 pointer-events-none"
                style={{
                  background: 'linear-gradient(105deg, transparent 40%, rgba(255,255,255,0.05) 50%, transparent 60%)',
                }}
              />
              
              {/* Label */}
              <span className="absolute inset-0 flex items-center justify-center text-white/55 text-sm font-light tracking-widest">
                {TILES[6].label}
              </span>
            </button>
          </div>
          
          {/* Guide Mode Section */}
          <div className="mt-10 text-center">
            <p className="text-white/30 text-sm mb-3">Not sure what you need?</p>
            <button
              onClick={() => setShowGuideModal(true)}
              className="px-6 py-2 rounded-full border border-white/10 text-white/50 hover:text-white/70 hover:border-white/20 transition-all text-sm"
              data-testid="meditation-guide-me"
            >
              Guide me
            </button>
          </div>
          
          {/* Quick Resets Section */}
          <div className="mt-10" data-testid="meditation-quick-resets">
            <h3 className="text-white/40 text-sm mb-4 text-center">Reset Right Now</h3>
            <div className="grid grid-cols-2 gap-2">
              {QUICK_RESETS.map((reset) => (
                <button
                  key={reset.id}
                  onClick={() => quickStart(reset.tileId, reset.duration)}
                  className="py-3 px-4 rounded-xl bg-white/[0.03] hover:bg-white/[0.06] border border-white/[0.05] text-white/50 hover:text-white/70 transition-all text-sm"
                  data-testid={`meditation-quick-${reset.id}`}
                >
                  {reset.label} – {reset.duration} min
                </button>
              ))}
            </div>
          </div>
          
          {/* More Toggle */}
          <div className="mt-10 text-center">
            <button
              onClick={() => setShowMoreSections(!showMoreSections)}
              className="inline-flex items-center gap-2 text-white/30 hover:text-white/50 text-sm transition-colors"
              data-testid="meditation-more-toggle"
            >
              {showMoreSections ? (
                <>
                  <ChevronUp className="w-4 h-4" />
                  Less
                </>
              ) : (
                <>
                  <ChevronDown className="w-4 h-4" />
                  More
                </>
              )}
            </button>
          </div>
          
          {/* Progressive Reveal Sections */}
          {showMoreSections && (
            <div className="mt-6 space-y-8 animate-fade-in" data-testid="meditation-more-sections">
              
              {/* Meditations for real moments */}
              <div>
                <h4 className="text-white/40 text-xs uppercase tracking-wider mb-3">
                  Meditations for real moments
                </h4>
                <div className="flex flex-wrap gap-2">
                  {REAL_MOMENTS.map((item, idx) => (
                    <button
                      key={idx}
                      onClick={() => quickStart(item.tileId, item.duration)}
                      className="py-2 px-4 rounded-full bg-white/[0.03] hover:bg-white/[0.06] border border-white/[0.05] text-white/40 hover:text-white/60 transition-all text-xs"
                    >
                      {item.label}
                    </button>
                  ))}
                </div>
              </div>
              
              {/* Micro Meditations */}
              <div>
                <h4 className="text-white/40 text-xs uppercase tracking-wider mb-3">
                  Micro Meditations
                </h4>
                <div className="flex flex-wrap gap-2">
                  {MICRO_MEDITATIONS.map((item, idx) => (
                    <button
                      key={idx}
                      onClick={() => quickStart(item.tileId, item.duration)}
                      className="py-2 px-4 rounded-full bg-white/[0.03] hover:bg-white/[0.06] border border-white/[0.05] text-white/40 hover:text-white/60 transition-all text-xs"
                    >
                      {item.label}
                    </button>
                  ))}
                </div>
              </div>
              
              {/* Sound & Atmosphere */}
              <div data-testid="meditation-sound-atmosphere">
                <h4 className="text-white/40 text-xs uppercase tracking-wider mb-3">
                  Sound & Atmosphere
                </h4>
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => setLightGuidance(!lightGuidance)}
                    className={`flex items-center gap-2 py-2 px-4 rounded-full border transition-all text-sm ${
                      lightGuidance 
                        ? 'border-white/20 bg-white/[0.05] text-white/60' 
                        : 'border-white/[0.05] text-white/40 hover:text-white/60'
                    }`}
                  >
                    {lightGuidance ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
                    Bell {lightGuidance ? 'On' : 'Off'}
                  </button>
                  
                  {/* Ambient sound options - rule 2: mutually exclusive */}
                  {AMBIENT_SOUNDS.map((sound) => (
                    <button
                      key={sound.id}
                      onClick={() => toggleAmbientSound(sound.id)}
                      className={`flex items-center gap-2 py-2 px-4 rounded-full border transition-all text-sm ${
                        ambientSound.selection === sound.id && ambientSound.enabled
                          ? 'border-white/20 bg-white/[0.05] text-white/60' 
                          : 'border-white/[0.05] text-white/40 hover:text-white/60'
                      }`}
                      data-testid={`meditation-ambient-${sound.id}`}
                    >
                      <span>{sound.emoji}</span>
                      {sound.label}
                      {ambientSound.selection === sound.id && ambientSound.enabled && (
                        <span className="text-white/40 text-xs ml-1">✓</span>
                      )}
                    </button>
                  ))}
                </div>
                {ambientSound.selection && ambientSound.enabled && (
                  <p className="text-white/25 text-xs mt-2">
                    {AMBIENT_SOUNDS.find(s => s.id === ambientSound.selection)?.label} will play during sessions
                  </p>
                )}
              </div>
            </div>
          )}
          
          {/* Your Rhythm (Soft Retention) */}
          {(rhythmStats.today > 0 || rhythmStats.week > 0) && (
            <div className="mt-12 text-center">
              <div className="inline-block px-6 py-4 rounded-2xl bg-white/[0.02] border border-white/[0.04]">
                <h4 className="text-white/30 text-xs uppercase tracking-wider mb-2">Your Rhythm</h4>
                <p className="text-white/40 text-sm">
                  {rhythmStats.today > 0 && (
                    <span>You paused {rhythmStats.today} time{rhythmStats.today !== 1 ? 's' : ''} today</span>
                  )}
                  {rhythmStats.today > 0 && rhythmStats.week > rhythmStats.today && <span> · </span>}
                  {rhythmStats.week > rhythmStats.today && (
                    <span>{rhythmStats.week} return{rhythmStats.week !== 1 ? 's' : ''} this week</span>
                  )}
                </p>
                <p className="text-white/25 text-xs mt-2">Short returns matter.</p>
              </div>
            </div>
          )}
          
        </div>
      </main>
      
      {/* CSS for animations */}
      <style>{`
        @keyframes fade-in {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        .animate-fade-in {
          animation: fade-in 0.5s ease-out forwards;
        }
        
        /* Tile entrance animation - fade in + rise */
        @keyframes tile-enter {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .meditation-tile {
          animation: tile-enter 500ms cubic-bezier(0.22, 1, 0.36, 1) forwards;
          animation-fill-mode: both;
        }
        
        /* Breathing glow - very slow, phase offset per tile */
        @keyframes breathe {
          0%, 100% { opacity: 0; }
          50% { opacity: 1; }
        }
        .meditation-breathe {
          animation: breathe 15s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
}
